import React, { useState } from 'react';
import Slack from '@svg/slack';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { GetCollectionsParams, getSlackChannels, SlackLoaderChannel } from '@requests/loaders';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../../../styles';
import { useParams } from 'react-router-dom';
import { RequestError } from '@requests/common';
import { Alert, Stack, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { Table } from '@common/Table';
import { GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;

  & .MuiDataGrid-row.Mui-selected {
    background-color: #e8f5fa !important;
  }
`;

const SlackChannelName = styled.div`
  color: #2c76ad;
  background-color: #e8f5fa;
  padding: 1px 2px;
  font-weight: 500;
`;

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    selected: SlackLoaderChannel[];
    setSelected: React.Dispatch<React.SetStateAction<SlackLoaderChannel[]>>;
    region: string;
    refreshInterval: string;
  };
}

export const SlackChannels: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const theme = useTheme();
  const userId = useSelector((state: State) => state.auth.user_id);
  const orgId = useSelector((state: State) => state.auth.org_id);

  const { service_id } = useParams();
  const { secretIdentifier: secretName, selected, setSelected } = configuration;

  const [filter, setFilter] = useState<'all' | 'selected'>('all');

  const {
    data: channels = [],
    isLoading,
    error,
  } = useQuery<SlackLoaderChannel[], RequestError, SlackLoaderChannel[], [string, GetCollectionsParams]>({
    queryKey: [
      'slack-channels',
      {
        secret_identifier: secretName,
        service_id: service_id as string,
        user_id: userId,
        org_id: orgId,
      },
    ],
    queryFn: getSlackChannels,
    enabled: Boolean(secretName && service_id),
    retry: false,
  });

  if (error) {
    return (
      <Modal
        open={open === 'slack' && page === 2}
        setOpen={reset}
        page={page + 1}
        total_pages={4}
        background={alpha(theme.text.standard, 0.06)}
        header='Slack Error'
        icon={<Slack color='#0057ff' height={21} />}
        body={<Alert severity='error'>{error?.response?.data?.error || error?.message}</Alert>}
        actions={
          <Stack direction='row' spacing={1}>
            <Button onClick={reset}>Cancel</Button>
            <Button customColor='red' onClick={() => setPage(0)}>
              Change Authentication Key
            </Button>
          </Stack>
        }
      />
    );
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Channel',
      flex: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<SlackLoaderChannel>) => {
        const { row } = params;

        return (
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <SlackChannelName>#{row.name}</SlackChannelName>
          </Stack>
        );
      },
    },
  ];

  const body = (
    <Container>
      <p>
        Select all the channels in your Slack account that you would like to include in this loader. Selecting a channel
        will pull all messages from that channel and upload them to your bucket. Max selection of 6 channels.
      </p>
      <Table
        data={channels.filter((channel) => filter === 'all' || selected.includes(channel))}
        noAutoWidth
        toolbarSearchOnly
        toolbarSearchMargin={0}
        selectionModel={selected.map((channel) => channel.id)}
        checkboxes
        isRowSelectable={(params: GridRowParams) => {
          const rowId = params.row.id;
          const isSelected = selected.some((channel) => channel.id === rowId);

          if (isSelected) return true;
          if (selected.length < 6) return true;
          return false;
        }}
        columns={columns}
        density={'standard'}
        sortModel='name'
        getRowId={(row) => row.id}
        loading={isLoading}
        height='600px'
        onSelectionModelChange={(newRows) => {
          if (newRows.length <= 6) {
            const newChannels = newRows.map((row) => channels.find((channel) => channel.id === row) || null);
            setSelected(newChannels.filter((channel) => channel !== null));
          }
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
      />
    </Container>
  );

  const actions = (
    <Stack width='100%' direction='row' display='flex' justifyContent='space-between'>
      <Stack>
        <ToggleButtonGroup
          size='small'
          color='primary'
          exclusive
          value={selected.length === 0 ? 'all' : filter}
          onChange={(_, value) => value && setFilter(value)}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton disabled={selected.length === 0} value='selected'>
            Selected
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack spacing={1} direction='row'>
        <Button onClick={() => setPage(1)}>Back</Button>
        <Button disabled={selected.length === 0} customColor='primary' onClick={() => setPage(3)}>
          Review Selection
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Modal
      open={open === 'slack' && page === 2}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Slack'
      icon={<Slack color={theme.text.standard} height={21} />}
      body={body}
      actions={actions}
    />
  );
};
